'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * A panel hanging off the bar: open state, and the two ways out every one of
 * them owes the reader — a pointer outside it, and Escape.
 *
 * Written twice before this (`AccountMenu` and `SignInPill`) and the copies had
 * already drifted: only one returned focus to the control that opened it.
 *
 * Pass a STABLE `onEscape` (useCallback) — it is a dependency, and a fresh
 * identity every render would resubscribe both listeners every render.
 */
export function useBarPanel({ onEscape }: { onEscape?: () => void } = {}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      onEscape?.();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onEscape]);

  return { open, setOpen, wrap };
}
